import { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
// ⚠️ Shared with the property page's zoning panel - see the note in zoningLabels.ts.
import {
  type Permission, type StatusNote, PERMISSION_LABEL, PERMISSION_STYLE,
  STATUS_NOTE_LABEL, STATUS_NOTE_SHORT, STATUS_NOTE_STYLE, prettyUse,
} from '../lib/zoningLabels';
import ZoneResearchPanel from '../components/zoning/ZoneResearchPanel';
import { FZ_USE_CASES } from '../lib/feasibilityLabels';

/**
 * Layer 2 of the land-play radar: what each city's zoning code permits.
 *
 * Two things this screen deliberately shows rather than hides:
 *
 *  1. **The gaps.** The coverage table lists every city+zone that exists in the property pool
 *     with the number of properties in it, whether or not anyone has researched it. That turns
 *     "we should look at zoning" into a ranked worklist - research Fontana M1 before Adelanto
 *     BP3, because one covers 39 properties and the other covers 2.
 *  2. **Staleness.** A rule verified two years ago is shown as stale rather than as fact.
 *     Municipal codes change, and a stale "CUP available" is worse than no rule at all because
 *     it gets taken to an owner.
 */

interface Rule {
  id: string;
  city: string;
  code: string;
  zone_name: string | null;
  use_case: string;
  permission: Permission;
  conditions: string | null;
  notes: string | null;
  code_section: string | null;
  source_url: string | null;
  verified_at: string | null;
  is_stale: boolean;
  review_status: 'draft' | 'approved';
  status_note: StatusNote | null;
  approved_at: string | null;
  jurisdiction: string | null;
}

interface Coverage {
  city: string;
  code: string;
  property_count: number;
  rule_count: number;
  approved_count: number;
}

export default function ZoningRules() {
  /**
   * ⚠️ MIRRORS THE SERVER'S `authorize(['admin'])` ON THE DELETE ROUTE. This is not the gate -
   * the gate is on the route, where it cannot be bypassed - it decides whether to OFFER the
   * control. Showing a broker a Remove link that 403s teaches him the app is broken; the server
   * check without this one is secure and unusable.
   */
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [removing, setRemoving] = useState<string | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [coverage, setCoverage] = useState<Coverage[]>([]);
  // ⚠️ Served by the API, not hardcoded here. See api.getZoningRules.
  const [useCases, setUseCases] = useState<string[]>([]);
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  // ⛔ The 141st's look: the nine FZ uses are approved only by the two-read rule - the server refuses a personal
  // sign-off on them - so the button counts and offers only the drafts it will actually sign.
  const signable = useMemo(() => rules.filter((r) => r.review_status === 'draft' && !FZ_USE_CASES.includes(r.use_case)), [rules]);
  const heldForPlanning = useMemo(() => rules.filter((r) => r.review_status === 'draft' && FZ_USE_CASES.includes(r.use_case)), [rules]);
  const [form, setForm] = useState({
    city: '',
    code: '',
    zone_name: '',
    use_case: 'truck_terminal',
    permission: 'unknown' as Permission,
    conditions: '',
    code_section: '',
    source_url: '',
    verified_at: '',
  });

  const load = async (forCity?: string) => {
    setLoading(true);
    setError('');
    try {
      const [r, c] = await Promise.all([
        api.getZoningRules(forCity ? { city: forCity } : undefined),
        api.getZoningCoverage(forCity || undefined),
      ]);
      setRules(r.rules);
      setCoverage(c.coverage);
      setUseCases(r.use_cases || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load zoning rules');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove ONE rule, permanently.
   *
   * ⛔⛔ THIS IS NOT THE TOOL FOR A WRONG ANSWER, AND THE CONFIRMATION SAYS SO. `flagRule` is:
   * it returns the rule to draft with a note prepended, so it stops answering IMMEDIATELY while
   * the research - the citations, the supremacy chain, the provenance of every inference -
   * survives for whoever picks it up. Deleting throws all of that away.
   *
   * 🚨 AND THERE IS NO WAY BACK. The project has no PITR and no restore point, so a deleted rule
   * is gone, not recoverable from a backup. Measured before wiring this: **zero duplicate keys**
   * across all 1,204 rules - the upsert on (city, code, use_case) already prevents the one case
   * that would routinely justify a delete. So this is for the genuine one-off: a rule typed
   * against the wrong city, or a zone that turned out not to exist. It should be rare, and the
   * confirmation is written to keep it that way.
   */
  const removeRule = async (r: Rule) => {
    const signed = r.review_status === 'approved';
    if (
      !window.confirm(
        `Permanently delete the ${prettyUse(r.use_case)} rule for ${r.city} ${r.code}?\n\n` +
          (signed ? 'This rule is SIGNED OFF and currently answers on property pages and brochures.\n' : '') +
          'The research goes with it - citations, conditions and provenance - and there is no ' +
          'restore point to recover it from.\n\n' +
          'If the answer is simply WRONG, use the flag control on the property page instead: ' +
          'that stops it answering right away and keeps the research.'
      )
    ) {
      return;
    }
    setRemoving(r.id);
    setError('');
    try {
      await api.deleteZoningRule(r.id);
      await load(city);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not delete the rule');
    } finally {
      setRemoving(null);
    }
  };

  /**
   * Sign off on a city. Until this runs, every rule below answers NOTHING to the radar - it is
   * fetched, seen to be a draft, and refused. The chips say which.
   */
  const approveCity = async () => {
    if (!city) return;
    const pending = signable.length;
    if (
      !window.confirm(
        `Sign off on ${pending} draft rule${pending === 1 ? '' : 's'} for ${city}?\n\n` +
          'These were drafted from the published code. Once approved they will drive ' +
          'recommendations, so check the citations first.'
      )
    ) {
      return;
    }
    setApproving(true);
    setError('');
    try {
      await api.approveZoningRules({ city });
      await load(city);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not approve the rules');
    } finally {
      setApproving(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  /**
   * ⚠️ THE UNION OF CITIES WITH PROPERTIES AND CITIES WITH RULES, not just the former.
   *
   * `coverage` is built from the PROPERTY pool, so a city with rules and no properties never
   * appeared here - and with the list capped at 1,000 rules, its rules were reachable from
   * nowhere on this screen. **That is exactly the row the delete control exists for**: a rule
   * typed against a wrong city has no properties by definition, so the one thing you most want
   * to remove was the one thing you could not navigate to. Found by trying to use the control
   * on a throwaway row, not by a test.
   */
  const cities = useMemo(
    () => [...new Set([...coverage.map((c) => c.city), ...rules.map((r) => r.city)])].sort(),
    [coverage, rules]
  );

  /** Rules grouped under their zone, so the page reads as a reference not an insertion log. */
  const byZone = useMemo(() => {
    const m = new Map<string, Rule[]>();
    for (const r of rules) {
      const key = `${r.city}::${r.code}`;
      const b = m.get(key);
      if (b) b.push(r);
      else m.set(key, [r]);
    }
    return m;
  }, [rules]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.saveZoningRule({
        ...form,
        conditions: form.conditions || null,
        code_section: form.code_section || null,
        source_url: form.source_url || null,
        verified_at: form.verified_at || null,
        zone_name: form.zone_name || null,
      });
      setForm((f) => ({ ...f, code: '', zone_name: '', conditions: '', code_section: '', source_url: '' }));
      await load(city || undefined);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save the rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-[1600px] space-y-6">
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Zoning Rules</h1>
          <p className="text-gray-500 mt-1">
            What each city's zoning code allows. Every rule carries a source and a date, because
            codes change.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">City</label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              void load(e.target.value || undefined);
            }}
          >
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {/* The sign-off, scoped to one city on purpose. Reviewing a city at a time is how
              Joseph asked for it, and a bare "approve everything" is how an unread city gets
              signed by accident - the API refuses that too. */}
          {city && signable.length > 0 && (
            <button
              type="button"
              onClick={() => void approveCity()}
              disabled={approving}
              className="ml-auto bg-success text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              {approving
                ? 'Signing off…'
                : `Sign off on ${signable.length} draft${signable.length === 1 ? '' : 's'} for ${city}`}
            </button>
          )}
        </div>
        {city && heldForPlanning.length > 0 && (
          <p className="text-xs text-gray-600">
            {`${heldForPlanning.length} ${heldForPlanning.length === 1 ? 'answer' : 'answers'} for the new building types wait on the City's Planning questions - they are approved only by the two-read rule, never signed off here.`}
          </p>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            {/* The worklist. Ranked by how many properties a zone covers, so research effort
                goes where it pays rather than alphabetically. */}
            <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Coverage</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Zones that exist in the property pool. Research the big ones first.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">City</th>
                      <th className="text-left px-4 py-2">Code</th>
                      <th className="text-right px-4 py-2">Properties</th>
                      <th className="text-left px-4 py-2 pl-6">Rules</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {coverage.slice(0, 40).map((c) => (
                      <tr key={`${c.city}-${c.code}`} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-900">{c.city}</td>
                        <td className="px-4 py-2 font-mono text-gray-700">{c.code}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{c.property_count}</td>
                        <td className="px-4 py-2 pl-6">
                          {/* "Researched" and "usable" are different states. A zone with 7
                              drafts and 0 signatures answers nothing, and showing only
                              "7 researched" would read as done. */}
                          {c.rule_count === 0 ? (
                            <span className="text-gray-400">not researched</span>
                          ) : c.approved_count === c.rule_count ? (
                            <span className="text-success">{c.approved_count} approved</span>
                          ) : (
                            <span className="text-warning">
                              {c.rule_count} drafted
                              {c.approved_count > 0 && `, ${c.approved_count} approved`}
                              {' — awaiting sign-off'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* FZ4: the city's size limits, notices and Planning questions. */}
            {city && <ZoneResearchPanel city={city} />}

            <div className="bg-white border border-gray-100 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Rules</h2>
              </div>
              {rules.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-gray-500">
                  No rules yet. Add one below — start with the zones covering the most properties.
                </p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {[...byZone.entries()].map(([key, group]) => (
                    <div key={key} className="px-4 py-3">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono font-medium text-gray-900">{group[0].code}</span>
                        <span className="text-sm text-gray-500">{group[0].city}</span>
                        {group[0].zone_name && (
                          <span className="text-sm text-gray-400">· {group[0].zone_name}</span>
                        )}
                        {/* ⚠️ Shown when the governing code book is NOT the city named on the
                            row. Fontana's two biggest candidate zones, IC and IR, are San
                            Bernardino COUNTY districts - Fontana's own code establishes only
                            M-1 and M-2. Reading a Fontana rule and assuming Fontana's code
                            applies is exactly the wrong turn this label prevents. */}
                        {group[0].jurisdiction &&
                          group[0].jurisdiction !== group[0].city && (
                            <span className="px-2 py-0.5 rounded border border-info/30 bg-info-wash text-info text-xs">
                              Governed by {group[0].jurisdiction}
                            </span>
                          )}
                      </div>
                      <div className="mt-2 space-y-2">
                        {group.map((r) => (
                          <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-gray-700 w-44">{prettyUse(r.use_case)}</span>
                            <span className={`px-2 py-0.5 rounded border text-xs ${PERMISSION_STYLE[r.permission]}`}>
                              {PERMISSION_LABEL[r.permission]}
                            </span>
                            {r.status_note && (
                              <span
                                title={STATUS_NOTE_LABEL[r.status_note]}
                                className={`px-2 py-0.5 rounded border text-xs ${STATUS_NOTE_STYLE[r.status_note]}`}
                              >
                                {STATUS_NOTE_SHORT[r.status_note]}
                              </span>
                            )}
                            {/* ⚠️ Distinct from stale, and it is the stronger statement. A stale
                                rule was once signed; a draft never was, and the radar refuses to
                                answer with it at all. */}
                            {r.review_status !== 'approved' && (
                              <span className="px-2 py-0.5 rounded border border-gray-300 bg-gray-100 text-gray-800 text-xs font-medium">
                                Draft — awaiting sign-off
                              </span>
                            )}
                            {r.is_stale && r.review_status === 'approved' && (
                              // Shown, never hidden. A stale rule saying "CUP available" is
                              // worse than no rule, because it gets acted on.
                              <span className="px-2 py-0.5 rounded border border-warning/30 bg-warning-wash text-warning text-xs">
                                {r.verified_at ? `Stale — checked ${r.verified_at}` : 'Never verified'}
                              </span>
                            )}
                            {r.source_url && (
                              <a
                                href={r.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-info hover:underline"
                              >
                                {r.code_section || 'source'}
                              </a>
                            )}
                            {/* ⛔ ADMIN ONLY, and last in the row on purpose - it is the one
                                irreversible control on this screen and nothing should reach it
                                on the way to something else. The usual answer to a wrong rule
                                is the flag control on the property page, which keeps the
                                research; see `removeRule`. */}
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => void removeRule(r)}
                                disabled={removing === r.id}
                                className="ml-auto text-xs text-gray-400 underline-offset-2 hover:text-primary-500 hover:underline disabled:opacity-50"
                              >
                                {removing === r.id ? 'Deleting...' : 'Delete'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Conditions on their own line rather than as tiny inline grey text -
                          they average ~680 characters and carry the whole substance of the rule. */}
                      {group.some((r) => r.conditions) && (
                        <details className="mt-2">
                          <summary className="text-xs text-info cursor-pointer select-none">
                            Show the code detail and conditions
                          </summary>
                          <div className="mt-2 space-y-2">
                            {group.filter((r) => r.conditions).map((r) => (
                              <div key={`c-${r.id}`} className="text-xs text-gray-700 leading-relaxed">
                                <span className="font-medium text-gray-900">{prettyUse(r.use_case)}: </span>
                                {r.conditions}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={submit} className="bg-white border border-gray-100 rounded-lg p-4 space-y-3">
              <h2 className="font-semibold text-gray-900">Add or update a rule</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <input required placeholder="City" className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <input required placeholder="Code (M-1, IC…)" className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                <input placeholder="Zone name" className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.zone_name} onChange={(e) => setForm({ ...form, zone_name: e.target.value })} />
                <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  value={form.use_case} onChange={(e) => setForm({ ...form, use_case: e.target.value })}>
                  {/* FZ-FIX I7: the nine FZ uses are read from the city's code twice - not typed here (Flag questions one). */}
                  {useCases.filter((u) => !FZ_USE_CASES.includes(u)).map((u) => <option key={u} value={u}>{prettyUse(u)}</option>)}
                </select>
                <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  value={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.value as Permission })}>
                  {(Object.keys(PERMISSION_LABEL) as Permission[]).map((p) => (
                    <option key={p} value={p}>{PERMISSION_LABEL[p]}</option>
                  ))}
                </select>
                <input placeholder="Code section" className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.code_section} onChange={(e) => setForm({ ...form, code_section: e.target.value })} />
                <input placeholder="Source URL" className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} />
                <input type="date" title="Verified on" className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.verified_at} onChange={(e) => setForm({ ...form, verified_at: e.target.value })} />
              </div>
              <input placeholder="Conditions (min lot size, screening, paving, hours…)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} />
              <div className="flex items-center gap-3">
                <button type="submit" disabled={saving}
                  className="bg-primary-500 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save rule'}
                </button>
                <p className="text-xs text-gray-500">
                  Leave the date empty if you have not confirmed it against the code yet — it will
                  show as never verified rather than as fact.
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </Layout>
  );
}
